/** Keep in lockstep with backend `analysis/hub_guards.py` HUB_TX_FETCH_CAP. */
export const HUB_TX_FETCH_CAP = 50;

/** Lean retry size after a gateway/network abort on a heavy address. */
export const TX_LOAD_RETRY_LIMIT = 20;

const HUB_NAME =
    /uniswap|pancakeswap|sushiswap|1inch|binance|okx|wazirx|huobi|htx|coinbase|kraken|bybit|kucoin|bitfinex|mexc|gate\.?io|universal router|tornado|wormhole|hot[_\s-]?wallet|off[-\s]?ramp/i;

function asTextList(value: unknown): string[] {
    if (value == null) return [];
    if (Array.isArray(value)) return value.map((v) => String(v ?? '')).filter(Boolean);
    if (typeof value === 'string') return value.trim() ? [value] : [];
    return [];
}

/** Flatten entity / case-label fields officers see as chips into one searchable blob. */
export function hubNodeSearchText(node: {
    entity_name?: string | null;
    entity_type?: string | null;
    entity_category?: string | null;
    label?: string | null;
    node_labels?: unknown;
    entity_tags?: unknown;
    tags?: unknown;
} | null | undefined): string {
    if (!node) return '';
    return [
        node.entity_name,
        node.entity_type,
        node.entity_category,
        node.label,
        ...asTextList(node.node_labels),
        ...asTextList(node.entity_tags),
        ...asTextList(node.tags),
    ]
        .filter(Boolean)
        .join(' ');
}

export function isHubExpandNode(node: {
    entity_name?: string | null;
    entity_type?: string | null;
    entity_category?: string | null;
    label?: string | null;
    node_labels?: unknown;
    entity_tags?: unknown;
    tags?: unknown;
} | null | undefined): boolean {
    if (!node) return false;
    const cat = String(node.entity_category || node.entity_type || '').toLowerCase();
    if (['exchange', 'defi', 'mixer', 'bridge', 'vasp'].includes(cat)) return true;
    return HUB_NAME.test(hubNodeSearchText(node));
}

export function hubExpandLimit(requested: number, node: Parameters<typeof isHubExpandNode>[0]): number {
    const n = Math.max(1, requested);
    if (!isHubExpandNode(node)) return n;
    return Math.min(n, HUB_TX_FETCH_CAP);
}

/**
 * Detect exchange hot / omnibus wallets (commingled pooled funds).
 * Expanding these on canvas causes commingling hairballs and obscures genuine fraud trails.
 */
export function isHotWalletNode(node: {
    entity_name?: string | null;
    entity_type?: string | null;
    entity_category?: string | null;
    label?: string | null;
    wallet_role?: string | null;
    subcategory?: string | null;
    node_labels?: unknown;
    entity_tags?: unknown;
    tags?: unknown;
    data?: any;
} | null | undefined): boolean {
    if (!node) return false;
    const role = String(node.wallet_role || node.data?.wallet_role || '').toLowerCase();
    if (role === 'hot' || role === 'hot_wallet') return true;

    const subcat = String(node.subcategory || node.data?.subcategory || '').toLowerCase();
    if (subcat === 'hot_wallet') return true;

    const search = hubNodeSearchText(node).toLowerCase();
    if (/hot[_\s-]?wallet|hotwallet/.test(search)) return true;

    const cat = String(node.entity_category || node.entity_type || node.data?.entity_type || '').toLowerCase();
    if (cat === 'exchange' || cat === 'cex') {
        const name = String(node.entity_name || node.label || node.data?.entity_name || '').toLowerCase();
        if (/\b(hot|omnibus|pooled)\b/.test(name)) return true;
        // Known numbered hot wallets e.g. "Binance 14", "Coinbase 10"
        if (/^(binance|coinbase|kraken|wazirx|coindcx|okx|huobi|htx|bybit|kucoin)\s+\d+$/i.test(name.trim())) {
            return true;
        }
    }
    return false;
}

/**
 * Detect generic cluster tags (OKLink / Arkham "<brand>. User" cluster notation).
 * These are ordinary user/suspect counterparties of an exchange cluster, NOT exchange-owned infrastructure.
 */
export function isClusterUserNode(node: {
    entity_name?: string | null;
    entity_type?: string | null;
    label?: string | null;
    node_labels?: unknown;
    entity_tags?: unknown;
    tags?: unknown;
    data?: any;
} | null | undefined): boolean {
    if (!node) return false;
    const tags = asTextList(node.entity_tags).concat(asTextList(node.tags), asTextList(node.node_labels));
    if (tags.some(t => t.toLowerCase() === 'unreliable_cluster_label' || t.toLowerCase() === 'cluster_counterparty')) {
        return true;
    }
    const name = String(node.entity_name || node.label || node.data?.entity_name || node.data?.label || '').trim();
    if (/[.\s\-_]+users?(?:[.\s\-_]*v?\d+)?\s*$/i.test(name)) {
        return true;
    }
    return false;
}

/**
 * True when Expand/Load should lean-retry once.
 * Axios often reports gateway kills as "Network Error" with no response; Railway/
 * edge proxies also return 408/502/503/504 with a body — those used to skip retry.
 */
export function isTxLoadNetworkFailure(error: unknown): boolean {
    const err = error as {
        response?: { status?: number };
        code?: string;
        message?: string;
    } | null | undefined;
    if (!err) return false;
    const status = err.response?.status;
    if (typeof status === 'number') {
        return status === 408 || status === 502 || status === 503 || status === 504;
    }
    const msg = String(err.message || '').toLowerCase();
    const code = String(err.code || '').toUpperCase();
    return (
        /network error|failed to fetch|err_network|econnaborted|etimedout|timeout/i.test(msg)
        || code === 'ERR_NETWORK'
        || code === 'ECONNABORTED'
        || code === 'ETIMEDOUT'
    );
}

export function txLoadFailureToast(error: unknown, node?: Parameters<typeof isHubExpandNode>[0]): string {
    const err = error as { response?: { status?: number; data?: { detail?: string } }; message?: string };
    const detail = err?.response?.data?.detail;
    const errMsg = (typeof detail === 'string' && detail) || err?.message || 'Unknown error';
    if (err?.response?.status === 429 || /rate/i.test(errMsg)) {
        return 'Blockchain API rate limit reached. Please wait 10-15 seconds and try again.';
    }
    if (err?.response?.status === 500) {
        return 'Server error loading transactions. The blockchain API may be temporarily unavailable.';
    }
    if (isTxLoadNetworkFailure(error)) {
        if (isHubExpandNode(node)) {
            return 'Exchange/hub wallet timed out loading transactions. Try Load by Count (20) in Transfers, or narrow a date range.';
        }
        return 'Failed to load transactions: connection timed out or dropped. Try Load by Count (20) in Transfers, or wait and retry.';
    }
    return `Failed to load transactions: ${errMsg}`;
}
